// @ts-check
// Tabela de registros (doc 06, seção 5): linhas de 32 px, cabeçalho e primeira coluna fixos,
// coluna de seleção, edição inline com teclado, linha "+ Adicionar novo", rolagem infinita por
// cursor (60 por página) e rodapé com a contagem. Paridade (contrato 08-contrato-kanban-tabela): rodapé de agregação
// por coluna, colunas que se redimensionam e se arrastam, seções por campo de seleção (seção 9) e os atalhos x,
// Shift+x, Ctrl/Cmd+A, Ctrl/Cmd+Enter e Delete/Backspace (seção 5.5).
import { html } from 'htm/preact';
import { isFieldLockedByStage, resolveStageTransition } from './stage-transition.js';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { findManyRecords, groupByRecords } from '../../data/rest.js';
import {
  createLiveRecordList,
  remoteListChangeState,
  createRecord,
  insertRecordLocally,
  mutationCounterState,
  updateRecordOptimistically,
} from '../../data/record-store.js';
import { currentUserPermissionsState, replaceViewLocally } from '../../data/metadata-store.js';
import { updateView } from '../../data/rest.js';
import {
  FIELD_TYPE,
  findOption,
  getFieldIcon,
  getLabelIdentifierField,
  isFieldDisplayable,
  isFieldFilterable,
  isFieldReadOnly,
  isFieldSortable,
  isFieldValueEmpty,
} from '../../lib/field-metadata.js';
import { formatNumber } from '../../lib/format.js';
import { getDefaultOperand } from '../../lib/view-filters.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { FieldDisplay } from './field-display.js';
import { FieldInput, INPUT_ONLY_FIELD_TYPES, getInputOnlyPatch, isFieldEditableInline } from './field-input.js';
import { RatingStars, ratingToNumber } from './field-display.js';
import { RecordChip, Tag } from '../../ui/chip.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import {
  Dropdown,
  DropdownMenuItems,
  DropdownMenuSearchInput,
  DropdownMenuSeparator,
  MenuItem,
} from '../../ui/dropdown.js';
import {
  addDraftFilter, clearViewDrilldown, getViewDraft, getViewQuery, isDeletedFilterActive, setDraftSort, setDeletedFilterActive,
} from '../views/view-state.js';
import { DrilldownEmptyState } from '../views/drilldown-chip.js';
import { drilldownModeOf } from '../../lib/drilldown-links.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { RecordSelectionActions } from './record-selection-actions.js';
import { TrashSelectionActions } from './trash-actions.js';
import { RecordContextMenu, selectionForContextMenu } from '../command-menu/record-context-menu.js';
import { ensureTrashSettingsLoaded, trashRetentionDaysState } from '../../data/trash.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';
import { canUpdateObjectRecords, findStageFilterOutsideScope } from '../../lib/permissions.js';
import { openRecord, registerRecordListProvider } from '../side-panel/open-record.js';
import { getTableGroupField } from '../views/view-options.js';
import { AggregateOperationMenu, fetchAggregateValues, formatAggregateValue, getAggregateShortLabel } from './aggregate-menu.js';
import { appendUniqueRecords, loadNextColumnPage, toColumnPageState } from './board-column-pages.js';
import {
  NO_VALUE_GROUP_KEY,
  applyColumnOrder,
  buildClearFieldPatch,
  clampTableColumnWidth,
  getDisplayedGroups,
  getRecordGroups,
  getTableColumns,
  moveIdBefore,
  selectIdRange,
  setSubFieldColumnVisibility,
  tableColumnIdOf,
  toggleSelectedId,
  updateSubFieldColumn,
  upsertViewField,
} from './view-layout.js';
import { isPostcodeProjection, isSubFieldProjection, projectSubField, sourceFieldIdOf } from '../../lib/address-sub-fields.js';
import { SUB_FIELD_COLUMN_MAX_SIZE, SUB_FIELD_COLUMN_MIN_SIZE, columnSubFieldNamesOf } from '../../../../shared/views/sub-field-columns.js';

// colunas da tabela, regra pura em view-layout.js (endereço em partes: CEP, Bairro, Cidade)
export { getTableColumns };

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {import('./view-layout.js').TableColumn} TableColumn
 * @typedef {import('./board-column-pages.js').ColumnPageState} GroupPageState
 * @typedef {{ key: string, value: string | null, label: string, color: string }} TableGroup
 */

const PAGE_SIZE = 60;
/** Coluna de uma parte do endereço (ou do endereço inteiro numa viewField em partes). @param {TableColumn} column */
const isPartColumn = (column) => column.subFieldName !== undefined;
/** Operação do rodapé: da parte, ou do viewField nas colunas comuns. @param {View} view @param {TableColumn} column */
const footerOperationOf = (view, column) => (isPartColumn(column)
  ? column.aggregateOperation ?? null
  : view.viewFields.find((viewField) => viewField.fieldMetadataId === column.field.id)?.aggregateOperation ?? null);
/** Largura de uma parte: os limites da validação da view (60 a 1000). @param {number} width */
const clampPartColumnWidth = (width) => Math.min(SUB_FIELD_COLUMN_MAX_SIZE, Math.max(SUB_FIELD_COLUMN_MIN_SIZE, Math.round(width)));
// seções da tabela agrupada carregam poucos registros por vez, como o Twenty (8); "Carregar mais" pede o resto
const GROUP_PAGE_SIZE = 10;
const DEFAULT_COLUMN_WIDTH = 150;
const PRINTABLE_KEY_PATTERN = /^.$/u;
// teto do "selecionar todos": ações em lote maiores ficam para importação ou automação
const MAXIMUM_SELECT_ALL_RECORDS = 10_000;
// ligado entre o pointerdown na alça de largura e o pointerup, para o cabeçalho não iniciar o arraste de coluna
let isResizingColumn = false;

/**
 * Na lixeira a tabela ganha "Excluído em" e "Excluído por" (o motor grava em updatedBy quem excluiu).
 * @param {ObjectMetadata} objectMetadata @param {TableColumn[]} columns
 * @returns {TableColumn[]}
 */
export function withTrashColumns(objectMetadata, columns) {
  const deletedAtField = objectMetadata.fields.find((field) => field.name === 'deletedAt');
  const updatedByField = objectMetadata.fields.find((field) => field.name === 'updatedBy');
  /** @type {TableColumn[]} */
  const trashColumns = [];
  if (deletedAtField) trashColumns.push({ field: { ...deletedAtField, label: t('trash.deletedAt') }, width: DEFAULT_COLUMN_WIDTH, viewFieldId: null, isTrashColumn: true });
  if (updatedByField) trashColumns.push({ field: { ...updatedByField, label: t('trash.deletedBy') }, width: DEFAULT_COLUMN_WIDTH, viewFieldId: null, isTrashColumn: true });
  // logo depois do nome, para não sumirem à direita em views com muitas colunas
  const [labelColumn, ...otherColumns] = columns.filter((column) => column.field.name !== 'updatedBy');
  return labelColumn ? [labelColumn, ...trashColumns, ...otherColumns] : trashColumns;
}

/**
 * Persiste imediatamente mudanças de colunas (visibilidade, ordem), como no Twenty.
 * @param {View} view
 * @param {View['viewFields']} viewFields
 */
export async function persistViewFields(view, viewFields) {
  replaceViewLocally({ ...view, viewFields });
  try {
    await updateView(view.id, { viewFields });
  } catch (error) {
    replaceViewLocally(view);
    showErrorSnackbar(error);
  }
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, column: TableColumn, columnIndex: number, columns: TableColumn[],
 *   onResizeStart: (column: TableColumn, event: PointerEvent) => void, dragState: { draggedFieldId: string | null, overFieldId: string | null },
 *   onColumnDragStart: (fieldId: string) => void, onColumnDragOver: (fieldId: string) => void, onColumnDrop: (fieldId: string) => void, onColumnDragEnd: () => void }} props
 */
function ColumnHeader({ objectMetadata, view, column, columnIndex, columns, onResizeStart, dragState, onColumnDragStart, onColumnDragOver, onColumnDrop, onColumnDragEnd }) {
  const anchorRef = useRef(/** @type {HTMLTableCellElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const { field } = column;
  const isLabelIdentifier = columnIndex === 0;
  const sourceFieldId = sourceFieldIdOf(field);
  const isPart = isPartColumn(column);
  // numa viewField em partes, o ícone de ordenação fica na parte cujo sub-campo é o da ordenação
  const draftSort = getViewDraft(view).sorts.value.find((sort) => sort.fieldMetadataId === sourceFieldId
    && (!isPart || (sort.subFieldName ?? null) === (column.subFieldName ?? null)));
  const label = translateMetadataLabel(field.label);
  const partHint = isSubFieldProjection(field) && hasTranslation(`familyPostcode.subFieldHints.${field.subFieldName}`)
    ? t(`familyPostcode.subFieldHints.${field.subFieldName}`) : null;
  const headerTitle = isSubFieldProjection(field)
    ? [t('familyPostcode.columnOption', { subField: label, field: translateMetadataLabel(field.sourceLabel) }), partHint].filter(Boolean).join('\n')
    : isPart ? t('familyPostcode.wholeAddress', { field: label }) : label;

  // mover uma parte troca com a vizinha do mesmo bloco; na borda do bloco, o bloco inteiro passa pela coluna vizinha
  const moveColumn = (/** @type {number} */ offset) => {
    const targetColumn = columns[columnIndex + offset];
    if (!targetColumn) return;
    const ids = columns.map(tableColumnIdOf);
    const blockIds = isPart ? columns.filter((candidate) => isPartColumn(candidate) && sourceFieldIdOf(candidate.field) === sourceFieldId).map(tableColumnIdOf) : [];
    const moving = blockIds.length > 1 && !blockIds.includes(tableColumnIdOf(targetColumn)) ? blockIds : [tableColumnIdOf(column)];
    const rest = ids.filter((id) => !moving.includes(id));
    const targetIndex = rest.indexOf(tableColumnIdOf(targetColumn));
    const insertAt = offset > 0 ? targetIndex + 1 : targetIndex;
    persistViewFields(view, applyColumnOrder(view.viewFields, [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)]));
  };

  return html`<th
    ref=${anchorRef}
    class=${`table-header-cell ${isLabelIdentifier ? 'is-sticky-label' : ''}`}
    data-open=${isOpen ? 'true' : undefined}
    data-field-name=${field.name}
    data-sub-field-name=${field.subFieldName ?? undefined}
    title=${headerTitle}
    data-dragging=${dragState.draggedFieldId === field.id ? 'true' : undefined}
    data-drop-before=${dragState.overFieldId === field.id && dragState.draggedFieldId !== field.id ? 'true' : undefined}
    draggable=${isLabelIdentifier ? undefined : 'true'}
    style=${{ width: `${column.width}px`, minWidth: `${column.width}px`, maxWidth: `${column.width}px` }}
    onClick=${() => setIsOpen(!isOpen)}
    onDragStart=${(/** @type {DragEvent} */ event) => {
      // a alça de largura fica dentro do cabeçalho arrastável: arrastar por ela redimensiona, não move a coluna
      if (isLabelIdentifier || isResizingColumn) {
        event.preventDefault();
        return;
      }
      event.dataTransfer?.setData('text/plain', field.id);
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
      onColumnDragStart(field.id);
    }}
    onDragOver=${(/** @type {DragEvent} */ event) => {
      if (!dragState.draggedFieldId || isLabelIdentifier) return;
      event.preventDefault();
      onColumnDragOver(field.id);
    }}
    onDrop=${(/** @type {DragEvent} */ event) => {
      if (!dragState.draggedFieldId || isLabelIdentifier) return;
      event.preventDefault();
      onColumnDrop(field.id);
    }}
    onDragEnd=${onColumnDragEnd}
  >
    <span class="table-header-cell-content">
      <${Icon} name=${getFieldIcon(field)} size=${16} />
      <span class="table-header-cell-label">${label}</span>
      ${draftSort ? html`<${Icon} name=${String(draftSort.direction).toLowerCase() === 'asc' ? 'IconArrowUp' : 'IconArrowDown'} size=${14} className="table-header-sort-icon" />` : null}
    </span>
    <span
      class="table-column-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label=${t('boardTable.table.resizeColumn', { field: label })}
      onPointerDown=${(/** @type {PointerEvent} */ event) => {
        isResizingColumn = true;
        onResizeStart(column, event);
      }}
      onClick=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}
    ></span>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${200}>
      <${DropdownMenuItems}>
        ${isFieldFilterable(field)
          ? html`<${MenuItem} icon="IconFilter" text=${t('table.columnMenu.filter')} onClick=${() => {
              setIsOpen(false);
              addDraftFilter(view, sourceFieldId, getDefaultOperand(field, field.subFieldName ?? null), field.subFieldName ?? null);
            }} />`
          : null}
        ${isFieldSortable(field)
          ? html`
              <${MenuItem} icon="IconSortAscending" text=${t('table.columnMenu.sortAscending')} onClick=${() => {
                setIsOpen(false);
                setDraftSort(view, sourceFieldId, 'asc', field.subFieldName ?? (isPart ? null : undefined));
              }} />
              <${MenuItem} icon="IconSortDescending" text=${t('table.columnMenu.sortDescending')} onClick=${() => {
                setIsOpen(false);
                setDraftSort(view, sourceFieldId, 'desc', field.subFieldName ?? (isPart ? null : undefined));
              }} />
            `
          : null}
        ${!isLabelIdentifier && !column.isTrashColumn
          ? html`
              <${DropdownMenuSeparator} />
              ${columnIndex > 1 ? html`<${MenuItem} icon="IconArrowLeft" text=${t('table.columnMenu.moveLeft')} onClick=${() => { setIsOpen(false); moveColumn(-1); }} />` : null}
              ${columnIndex < columns.length - 1 ? html`<${MenuItem} icon="IconArrowRight" text=${t('table.columnMenu.moveRight')} onClick=${() => { setIsOpen(false); moveColumn(1); }} />` : null}
              <${MenuItem} icon="IconEyeOff" text=${t('table.columnMenu.hide')} onClick=${() => {
                setIsOpen(false);
                persistViewFields(view, isPart
                  ? setSubFieldColumnVisibility(view.viewFields, sourceFieldId, column.subFieldName ?? null, false)
                  : view.viewFields.map((viewField) => (viewField.fieldMetadataId === field.id ? { ...viewField, isVisible: false } : viewField)));
              }} />
            `
          : null}
      <//>
    <//>
  </th>`;
}

/**
 * Botão "+" no fim do cabeçalho: mostra campos ocultos para torná-los visíveis.
 * @param {{ objectMetadata: ObjectMetadata, view: View, columns: TableColumn[] }} props
 */
function AddColumnHeader({ objectMetadata, view, columns }) {
  const anchorRef = useRef(/** @type {HTMLTableCellElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const visibleIds = new Set(columns.map((column) => column.field.id));
  const visibleSourceIds = new Set(columns.map((column) => sourceFieldIdOf(column.field)));
  const matchesSearch = (/** @type {string} */ text) => normalizeSearchText(text).includes(normalizeSearchText(searchText));
  const hiddenFields = objectMetadata.fields.filter(
    (field) =>
      isFieldDisplayable(field) &&
      !visibleSourceIds.has(field.id) &&
      field.type !== FIELD_TYPE.RICH_TEXT &&
      matchesSearch(translateMetadataLabel(field.label)),
  );
  // partes do endereço ainda não mostradas ("CEP (Endereço da família)") e o endereço inteiro quando ele está em partes
  const partOptions = objectMetadata.fields
    .filter((field) => isFieldDisplayable(field) && columnSubFieldNamesOf(field.type).length > 0)
    .flatMap((field) => {
      const fieldLabel = translateMetadataLabel(field.label);
      const isSplit = columns.some((column) => isPartColumn(column) && sourceFieldIdOf(column.field) === field.id);
      const parts = columnSubFieldNamesOf(field.type)
        .filter((subFieldName) => !visibleIds.has(`${field.id}.${subFieldName}`))
        .map((subFieldName) => ({
          key: `${field.id}.${subFieldName}`, field, subFieldName, icon: projectSubField(field, subFieldName).icon ?? getFieldIcon(field),
          text: t('familyPostcode.columnOption', { subField: t(`familyPostcode.subFields.${subFieldName}`), field: fieldLabel }),
        }));
      const whole = isSplit && !visibleIds.has(field.id)
        ? [{ key: field.id, field, subFieldName: null, icon: getFieldIcon(field), text: t('familyPostcode.wholeAddress', { field: fieldLabel }) }]
        : [];
      return [...parts, ...whole];
    })
    .filter((option) => matchesSearch(option.text));
  const showField = (/** @type {FieldMetadata} */ field) => {
    const existing = view.viewFields.find((viewField) => viewField.fieldMetadataId === field.id);
    const maxPosition = Math.max(0, ...view.viewFields.map((viewField) => viewField.position));
    const viewFields = existing
      ? view.viewFields.map((viewField) => (viewField.fieldMetadataId === field.id ? { ...viewField, isVisible: true, position: maxPosition + 1 } : viewField))
      : [...view.viewFields, { id: crypto.randomUUID(), fieldMetadataId: field.id, position: maxPosition + 1, isVisible: true, size: 100 }];
    persistViewFields(view, viewFields);
  };
  return html`<th ref=${anchorRef} class="table-header-cell table-add-column-cell" onClick=${() => setIsOpen(!isOpen)}>
    <span class="table-header-cell-content" title=${t('table.addColumn')}><${Icon} name="IconPlus" size=${16} /></span>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${240} placement="bottom-end">
      <${DropdownMenuSearchInput} value=${searchText} onChange=${setSearchText} placeholder=${t('viewBar.searchFields')} />
      <${DropdownMenuSeparator} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${hiddenFields.length === 0 && partOptions.length === 0 ? html`<div class="menu-item-empty">${searchText ? t('common.noResults') : t('table.allFieldsVisible')}</div>` : null}
        ${hiddenFields.map((field) => html`<${MenuItem} key=${field.id} icon=${getFieldIcon(field)} text=${translateMetadataLabel(field.label)} onClick=${() => showField(field)} />`)}
        ${partOptions.map((option) => html`<${MenuItem} key=${option.key} icon=${option.icon} text=${option.text} onClick=${() => {
          setIsOpen(false);
          persistViewFields(view, setSubFieldColumnVisibility(view.viewFields, option.field.id, option.subFieldName, true));
        }} />`)}
      <//>
    <//>
  </th>`;
}

/**
 * Célula do rodapé (doc 06, seção 5.6): mostra a operação escolhida e o valor; o clique escolhe a operação e grava
 * em viewField.aggregateOperation. Na primeira coluna, sem operação, fica a contagem de registros.
 * @param {{ view: View, column: TableColumn, columnIndex: number, operation: string | null, value: unknown, totalCount: number }} props
 */
function TableFooterCell({ view, column, columnIndex, operation, value, totalCount }) {
  const anchorRef = useRef(/** @type {HTMLTableCellElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const isLabelColumn = columnIndex === 0;
  return html`<td
    ref=${anchorRef}
    class=${`table-cell table-footer-cell ${isLabelColumn ? 'is-sticky-label' : ''}`}
    data-open=${isOpen ? 'true' : undefined}
    data-field-name=${column.field.name}
    style=${{ width: `${column.width}px`, maxWidth: `${column.width}px` }}
    onClick=${() => setIsOpen(!isOpen)}
  >
    <div class="table-cell-content table-footer-count">
      ${operation
        ? html`<span class="table-footer-label">${getAggregateShortLabel(operation)}</span><span>${formatAggregateValue(column.field, operation, value)}</span>`
        : isLabelColumn
          ? html`<span class="table-footer-label">${t('table.footer.countLabel')}</span><span>${formatNumber(totalCount)}</span>`
          : html`<span class="table-footer-placeholder">${t('boardTable.table.calculate')}</span>`}
    </div>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${220} placement="top-start">
      <${AggregateOperationMenu} field=${column.field} selected=${operation} onSelect=${(/** @type {string | null} */ nextOperation) => {
        setIsOpen(false);
        persistViewFields(view, isPartColumn(column)
          ? updateSubFieldColumn(view.viewFields, sourceFieldIdOf(column.field), column.subFieldName ?? null, { aggregateOperation: nextOperation })
          : upsertViewField(view.viewFields, column.field.id, { aggregateOperation: nextOperation }));
      }} />
    <//>
  </td>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, onTotalCountChange?: (count: number | null) => void, onRequestCreate?: (prefill?: Record<string, unknown>) => void,
 *   onSelectionChange?: (count: number) => void }} props
 */
export function RecordTable({ objectMetadata, view, onTotalCountChange, onRequestCreate, onSelectionChange }) {
  // na lixeira, excluídos por outra pessoa continuam na lista (atualização automática, contrato 08 de navegação)
  const isTrashModeRef = useRef(false);
  const liveList = useMemo(() => createLiveRecordList(objectMetadata.nameSingular, [], { isTrash: () => isTrashModeRef.current }), [objectMetadata.nameSingular, view.id]);
  useEffect(() => () => liveList.dispose(), [liveList]);
  const records = liveList.records.value;

  const [pageInfo, setPageInfo] = useState(/** @type {{ hasNextPage: boolean, endCursor?: string | null }} */ ({ hasNextPage: false }));
  const [totalCount, setTotalCount] = useState(/** @type {number | null} */ (null));
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(/** @type {Set<string>} */ (new Set()));
  const [editingCell, setEditingCell] = useState(/** @type {{ recordId: string, fieldId: string, initialCharacter?: string | null } | null} */ (null));
  const [focusedCell, setFocusedCell] = useState(/** @type {{ recordId: string, columnIndex: number } | null} */ (null));
  const [draftRow, setDraftRow] = useState(/** @type {Record<string, any> | null} */ (null));
  // "selecionar todos os N": a seleção passa a ser o filtro da view, inclusive linhas ainda não carregadas
  const [isAllMatchingSelected, setIsAllMatchingSelected] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // largura em arraste da alça de uma coluna; ao soltar vai para o viewField
  const [resizing, setResizing] = useState(/** @type {{ fieldId: string, width: number } | null} */ (null));
  const [columnDrag, setColumnDrag] = useState(/** @type {{ draggedFieldId: string | null, overFieldId: string | null }} */ ({ draggedFieldId: null, overFieldId: null }));
  const [aggregateValues, setAggregateValues] = useState(/** @type {Map<string, unknown>} */ (new Map()));
  // tabela agrupada: contagem por grupo, cursor de cada seção e seções recolhidas
  const [groupCounts, setGroupCounts] = useState(/** @type {Map<string, number>} */ (new Map()));
  const [groupPages, setGroupPages] = useState(/** @type {Map<string, GroupPageState>} */ (new Map()));
  const [collapsedGroups, setCollapsedGroups] = useState(/** @type {Set<string>} */ (new Set()));
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const sentinelRef = useRef(/** @type {HTMLTableRowElement | null} */ (null));
  const requestIdRef = useRef(0);
  // última linha marcada com x, de onde o Shift+x estende o intervalo
  const selectionAnchorRef = useRef(/** @type {string | null} */ (null));

  // lixeira (contrato 08 da lixeira): só excluídos, somente leitura, com Restaurar e Excluir definitivamente
  const isTrashMode = isDeletedFilterActive(view);
  isTrashModeRef.current = isTrashMode;
  // recarga em silêncio quando outra pessoa cria registros: sem esqueleto e sem perder seleção nem edição
  const silentReloadRef = useRef(false);
  const baseColumns = getTableColumns(objectMetadata, view);
  const viewColumns = resizing ? baseColumns.map((column) => (column.field.id === resizing.fieldId ? { ...column, width: resizing.width } : column)) : baseColumns;
  const columns = isTrashMode ? withTrashColumns(objectMetadata, viewColumns) : viewColumns;
  const query = getViewQuery(view);
  // recorte de um painel por snapshot: os ids são os do clique, e um registro criado aqui não entraria na lista
  const isSnapshotScope = drilldownModeOf(query.drilldown) === 'SNAPSHOT';
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  useEffect(() => {
    if (isTrashMode) ensureTrashSettingsLoaded();
  }, [isTrashMode]);
  const labelField = getLabelIdentifierField(objectMetadata);
  const canUpdate = canUpdateObjectRecords(objectMetadata);

  // a lixeira é sempre uma lista simples, mesmo numa view agrupada
  const groupField = isTrashMode ? null : getTableGroupField(view);
  const isGrouped = Boolean(groupField);
  /** @type {TableGroup[]} */
  const visibleGroups = groupField
    ? getRecordGroups(view.viewGroups, groupField).filter((group) => group.isVisible).map((group) => {
        if (group.value === null) return { key: NO_VALUE_GROUP_KEY, value: null, label: t('board.noValue'), color: 'transparent' };
        const option = findOption(groupField, group.value);
        return { key: group.key, value: group.value, label: translateMetadataLabel(option?.label ?? group.value), color: option?.color ?? 'gray' };
      })
    : [];
  const groupSignature = visibleGroups.map((group) => group.key).join(',');
  const groupPredicate = (/** @type {TableGroup} */ group) => ({
    field: /** @type {FieldMetadata} */ (groupField).name, operator: group.value === null ? 'is' : 'eq', value: group.value === null ? 'NULL' : group.value,
  });
  const groupFilter = (/** @type {TableGroup} */ group) => (query.filter ? { and: [query.filter, groupPredicate(group)] } : groupPredicate(group));
  // agrupada, o rodapé e "todos" valem só para os grupos visíveis (Não é lead fica de fora)
  const effectiveFilter = isGrouped && visibleGroups.length > 0
    ? (query.filter ? { and: [query.filter, { or: visibleGroups.map(groupPredicate) }] } : { or: visibleGroups.map(groupPredicate) })
    : query.filter;
  const groupKeyOf = (/** @type {any} */ record) => (groupField && !isFieldValueEmpty(groupField, record[groupField.name]) ? String(record[groupField.name]) : NO_VALUE_GROUP_KEY);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const isSilent = silentReloadRef.current;
    silentReloadRef.current = false;
    setLoadError(null);
    if (!isSilent) {
      setIsLoading(true);
      setSelectedIds(new Set());
      setIsAllMatchingSelected(false);
      setEditingCell(null);
    }
    const load = groupField
      ? Promise.all(visibleGroups.map((group) => findManyRecords(objectMetadata, { filter: groupFilter(group), orderBy: query.orderBy, limit: GROUP_PAGE_SIZE, depth: 1, drilldown: query.drilldown })))
        .then((results) => {
          if (requestId !== requestIdRef.current) return;
          liveList.records.value = results.flatMap((result) => result.records);
          setGroupPages(new Map(visibleGroups.map((group, index) => [group.key, toColumnPageState(results[index])])));
          setPageInfo({ hasNextPage: false });
        })
      : findManyRecords(objectMetadata, { filter: query.filter, orderBy: query.orderBy, limit: PAGE_SIZE, depth: 1, drilldown: query.drilldown })
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          liveList.records.value = result.records;
          setPageInfo(result.pageInfo);
          setTotalCount(result.totalCount);
        });
    load
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        liveList.records.value = [];
        setTotalCount(0);
        // o recorte do painel que não abre vira o estado vazio dele (sem o aviso solto no canto)
        if (query.drilldown) setLoadError(error);
        else showErrorSnackbar(error);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false);
      });
  }, [objectMetadata.id, view.id, query.key, liveList, reloadToken, groupField?.id, groupSignature]);

  // registro novo de outra pessoa: relê a primeira página em silêncio, se a pessoa não rolou além dela nem está editando
  const remoteListVersion = remoteListChangeState.value[objectMetadata.nameSingular] ?? 0;
  const remoteListVersionRef = useRef(remoteListVersion);
  useEffect(() => {
    if (remoteListVersion === remoteListVersionRef.current) return;
    remoteListVersionRef.current = remoteListVersion;
    if (isLoading || editingCell || draftRow || (!groupField && records.length > PAGE_SIZE)) return;
    silentReloadRef.current = true;
    setReloadToken((token) => token + 1);
  }, [remoteListVersion]);

  // contagem de cada seção pelo mesmo groupBy do kanban; o total da página soma só as seções visíveis
  useEffect(() => {
    if (!groupField) return undefined;
    let isCancelled = false;
    groupByRecords(objectMetadata, { groupByFieldName: groupField.name, filter: query.filter, drilldown: query.drilldown })
      .then((result) => {
        if (isCancelled) return;
        const counts = new Map(result.map((group) => [
          group.value === null || group.value === undefined || group.value === '' ? NO_VALUE_GROUP_KEY : String(group.value), Number(group.count ?? 0),
        ]));
        setGroupCounts(counts);
        setTotalCount(visibleGroups.reduce((sum, group) => sum + (counts.get(group.key) ?? 0), 0));
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [objectMetadata.id, view.id, query.key, groupField?.id, groupSignature, mutationCounterState.value, reloadToken]);

  // rodapé: uma consulta para as operações escolhidas nas colunas, com o filtro da view
  const aggregateRequests = columns
    .map((column) => {
      const operation = footerOperationOf(view, column);
      // parte do endereço: a operação vale para o sub-campo (COUNT_EMPTY:householdAddress.addressPostcode)
      return operation ? { field: column.field, operation, ...(isSubFieldProjection(column.field) ? { subFieldName: column.field.subFieldName } : {}) } : null;
    })
    .filter((request) => request !== null);
  const aggregateKey = aggregateRequests.map((request) => `${request.operation}:${request.field.id}`).join(',');
  useEffect(() => {
    if (!aggregateKey) {
      setAggregateValues(new Map());
      return undefined;
    }
    let isCancelled = false;
    fetchAggregateValues(objectMetadata, effectiveFilter, aggregateRequests, query.drilldown)
      .then(({ values }) => {
        if (!isCancelled) setAggregateValues(new Map(aggregateRequests.map((request, index) => [request.field.id, values[index]])));
      })
      .catch(() => {
        if (!isCancelled) setAggregateValues(new Map());
      });
    return () => {
      isCancelled = true;
    };
  }, [objectMetadata.id, view.id, query.key, aggregateKey, groupSignature, mutationCounterState.value, reloadToken]);

  useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount]);

  // ordem de exibição: na tabela agrupada, os registros das seções abertas, seção por seção
  const displayedGroups = getDisplayedGroups(
    visibleGroups.map((group, index) => ({ key: group.key, value: group.value, isVisible: true, position: index })),
    (key) => (isLoading ? undefined : groupCounts.get(key) ?? records.filter((record) => groupKeyOf(record) === key).length),
    Boolean(view.shouldHideEmptyGroups),
  ).map((group) => /** @type {TableGroup} */ (visibleGroups.find((candidate) => candidate.key === group.key)));
  const recordsByGroup = new Map(displayedGroups.map((group) => [group.key, records.filter((record) => groupKeyOf(record) === group.key)]));
  const orderedRecords = isGrouped
    ? displayedGroups.flatMap((group) => (collapsedGroups.has(group.key) ? [] : recordsByGroup.get(group.key) ?? []))
    : records;

  // lista da view para o anterior e próximo da página do registro (contrato da página de registro, seção 2)
  /** @param {string} _recordId @returns {import('../../lib/record-navigation.js').RecordListContext} */
  const buildListContext = (_recordId) => ({
    objectNameSingular: objectMetadata.nameSingular,
    recordIds: orderedRecords.map((record) => record.id),
    totalCount: isGrouped ? orderedRecords.length : totalCount,
    hasNextPage: !isGrouped && Boolean(pageInfo.hasNextPage),
    endCursor: isGrouped ? null : pageInfo.endCursor ?? null,
    filter: query.filter,
    orderBy: query.orderBy,
    drilldown: query.drilldown,
    viewName: view.name ?? null,
  });
  const listContextRef = useRef(buildListContext);
  listContextRef.current = buildListContext;
  useEffect(() => registerRecordListProvider({ element: () => scrollRef.current, getContext: (recordId) => listContextRef.current(recordId) }), []);

  const selectedCount = isAllMatchingSelected ? totalCount ?? selectedIds.size : selectedIds.size;
  useEffect(() => {
    onSelectionChange?.(selectedCount);
  }, [selectedCount]);

  /** Ids da seleção; com "todos", percorre as páginas do filtro da view (sem relações, 200 por vez). */
  const resolveSelectedIds = async () => {
    if (!isAllMatchingSelected) return [...selectedIds];
    /** @type {string[]} */
    const ids = [];
    let cursor = /** @type {string | null | undefined} */ (undefined);
    do {
      const page = await findManyRecords(objectMetadata, { filter: query.filter, orderBy: query.orderBy, limit: 200, depth: 0, startingAfter: cursor, drilldown: query.drilldown });
      ids.push(...page.records.map((record) => record.id));
      cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    } while (cursor && ids.length < MAXIMUM_SELECT_ALL_RECORDS);
    return ids;
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setIsAllMatchingSelected(false);
    selectionAnchorRef.current = null;
  };

  // clique direito (contrato 08-contrato-comandos-atalhos, seção 3): linha fora da seleção vira a seleção
  const [contextMenu, setContextMenu] = useState(/** @type {{ x: number, y: number } | null} */ (null));
  const openContextMenu = (/** @type {MouseEvent} */ event, /** @type {any} */ record) => {
    if (editingCell || /** @type {HTMLElement} */ (event.target).closest('input, textarea, [contenteditable="true"]')) return;
    event.preventDefault();
    const { ids, keepsSelection } = selectionForContextMenu(selectedIds, record.id);
    if (!keepsSelection) {
      setIsAllMatchingSelected(false);
      setSelectedIds(ids);
      selectionAnchorRef.current = record.id;
    }
    setContextMenu({ x: event.clientX, y: event.clientY });
  };
  const removeLocally = (/** @type {string[]} */ ids) => {
    const removed = new Set(ids);
    liveList.records.value = liveList.records.value.filter((record) => !removed.has(record.id));
    setTotalCount((count) => (count === null ? count : Math.max(0, count - ids.length)));
    clearSelection();
  };

  const loadMore = () => {
    if (isGrouped || !pageInfo.hasNextPage || isLoadingMore || isLoading) return;
    const requestId = requestIdRef.current;
    setIsLoadingMore(true);
    findManyRecords(objectMetadata, { filter: query.filter, orderBy: query.orderBy, limit: PAGE_SIZE, depth: 1, startingAfter: pageInfo.endCursor, drilldown: query.drilldown })
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        const existingIds = new Set(liveList.records.value.map((record) => record.id));
        liveList.records.value = [...liveList.records.value, ...result.records.filter((record) => !existingIds.has(record.id))];
        setPageInfo(result.pageInfo);
      })
      .catch(showErrorSnackbar)
      .finally(() => setIsLoadingMore(false));
  };

  /** @param {TableGroup} group */
  const loadMoreInGroup = async (group) => {
    const state = groupPages.get(group.key);
    if (!state?.hasNextPage || state.isLoadingMore) return;
    const requestId = requestIdRef.current;
    setGroupPages((current) => new Map(current).set(group.key, { ...state, isLoadingMore: true }));
    try {
      const page = await loadNextColumnPage(
        (options) => findManyRecords(objectMetadata, { ...options, limit: GROUP_PAGE_SIZE }),
        { filter: groupFilter(group), orderBy: query.orderBy, drilldown: query.drilldown },
        state,
      );
      if (requestId !== requestIdRef.current) return;
      liveList.records.value = appendUniqueRecords(liveList.records.value, page.records);
      setGroupPages((current) => new Map(current).set(group.key, page.state));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      showErrorSnackbar(error);
      setGroupPages((current) => new Map(current).set(group.key, { ...state, isLoadingMore: false }));
    }
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !pageInfo.hasNextPage) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { root: scrollRef.current, rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pageInfo, isLoadingMore, isLoading, records.length]);

  const commitPatch = async (/** @type {any} */ record, /** @type {import('./field-input.js').FieldPatch} */ requestedPatch) => {
    const patch = await resolveStageTransition(objectMetadata, record, requestedPatch);
    if (!patch) return;
    updateRecordOptimistically(objectMetadata, record.id, patch.apiPatch, patch.localPatch).catch(() => {});
  };

  const moveFocus = (/** @type {string} */ recordId, /** @type {number} */ columnIndex, /** @type {import('./field-input.js').EditNavigation} */ navigation) => {
    const rowIndex = orderedRecords.findIndex((record) => record.id === recordId);
    if (navigation === 'down') {
      const nextRecord = orderedRecords[rowIndex + 1];
      setFocusedCell({ recordId: nextRecord ? nextRecord.id : recordId, columnIndex });
    } else if (navigation === 'next') {
      setFocusedCell({ recordId, columnIndex: Math.min(columns.length - 1, columnIndex + 1) });
    } else if (navigation === 'previous') {
      setFocusedCell({ recordId, columnIndex: Math.max(0, columnIndex - 1) });
    } else {
      setFocusedCell({ recordId, columnIndex });
    }
    scrollRef.current?.focus({ preventScroll: true });
  };

  // somente leitura: papel sem edição, campo de sistema ou travado pela etapa (data da visita depois da visita)
  const isCellReadOnly = (/** @type {any} */ record, /** @type {TableColumn | undefined} */ column) =>
    isTrashMode || !column || isFieldReadOnly(column.field) || !isFieldEditableInline(column.field) || isFieldLockedByStage(objectMetadata, column.field, record);

  const openCell = (/** @type {any} */ record, /** @type {number} */ columnIndex, /** @type {string | null} */ initialCharacter = null) => {
    const column = columns[columnIndex];
    if (isTrashMode) return;
    if (!column || isCellReadOnly(record, column)) return;
    if (INPUT_ONLY_FIELD_TYPES.has(column.field.type)) {
      if (column.field.type === FIELD_TYPE.BOOLEAN) commitPatch(record, getInputOnlyPatch(column.field, record));
      return;
    }
    setFocusedCell({ recordId: record.id, columnIndex });
    setEditingCell({ recordId: record.id, fieldId: column.field.id, initialCharacter });
  };

  const selectAllLoaded = () => {
    setIsAllMatchingSelected(false);
    setSelectedIds(new Set(orderedRecords.map((record) => record.id)));
  };

  /** @param {KeyboardEvent} event */
  const handleTableKeyDown = (event) => {
    if (editingCell) return;
    const isModifier = event.metaKey || event.ctrlKey;
    if (isModifier && !event.altKey && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      selectAllLoaded();
      return;
    }
    if (!focusedCell) {
      if (event.key === 'Escape' && selectedIds.size > 0) clearSelection();
      return;
    }
    const rowIndex = orderedRecords.findIndex((record) => record.id === focusedCell.recordId);
    if (rowIndex < 0) return;
    const record = orderedRecords[rowIndex];
    if (isModifier && event.key === 'Enter') {
      event.preventDefault();
      openRecord({ objectNameSingular: objectMetadata.nameSingular, recordId: record.id, listContext: buildListContext(record.id) });
      return;
    }
    if (!isModifier && !event.altKey && event.key.toLowerCase() === 'x') {
      event.preventDefault();
      setIsAllMatchingSelected(false);
      setSelectedIds(event.shiftKey
        ? selectIdRange(orderedRecords.map((candidate) => candidate.id), selectionAnchorRef.current, record.id, selectedIds)
        : toggleSelectedId(selectedIds, record.id));
      selectionAnchorRef.current = record.id;
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (orderedRecords[rowIndex + 1]) setFocusedCell({ recordId: orderedRecords[rowIndex + 1].id, columnIndex: focusedCell.columnIndex });
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (orderedRecords[rowIndex - 1]) setFocusedCell({ recordId: orderedRecords[rowIndex - 1].id, columnIndex: focusedCell.columnIndex });
        break;
      case 'ArrowRight':
        event.preventDefault();
        setFocusedCell({ recordId: record.id, columnIndex: Math.min(columns.length - 1, focusedCell.columnIndex + 1) });
        break;
      case 'ArrowLeft':
        event.preventDefault();
        setFocusedCell({ recordId: record.id, columnIndex: Math.max(0, focusedCell.columnIndex - 1) });
        break;
      case 'Enter':
        event.preventDefault();
        openCell(record, focusedCell.columnIndex);
        break;
      case 'Delete':
      case 'Backspace': {
        event.preventDefault();
        const column = columns[focusedCell.columnIndex];
        // o nome do registro não se apaga pelo teclado; os outros seguem as mesmas regras da edição
        if (focusedCell.columnIndex === 0 || isCellReadOnly(record, column)) break;
        const patch = buildClearFieldPatch(/** @type {TableColumn} */ (column).field, record);
        if (patch) commitPatch(record, patch);
        break;
      }
      case 'Escape':
        setFocusedCell(null);
        if (selectedIds.size > 0) clearSelection();
        break;
      default:
        if (PRINTABLE_KEY_PATTERN.test(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
          const column = columns[focusedCell.columnIndex];
          // na coluna do CEP, um dígito abre o editor com ele; nas outras partes do endereço, qualquer tecla
          const opensPart = column && isSubFieldProjection(column.field) && (!isPostcodeProjection(column.field) || /\d/.test(event.key));
          if (column && (opensPart || [FIELD_TYPE.TEXT, FIELD_TYPE.NUMBER, FIELD_TYPE.FULL_NAME, FIELD_TYPE.EMAILS, FIELD_TYPE.PHONES, FIELD_TYPE.SELECT, FIELD_TYPE.CURRENCY].includes(column.field.type))) {
            event.preventDefault();
            openCell(record, focusedCell.columnIndex, event.key);
          }
        }
    }
  };

  const allSelected = orderedRecords.length > 0 && orderedRecords.every((record) => selectedIds.has(record.id));
  const someSelected = !allSelected && orderedRecords.some((record) => selectedIds.has(record.id));

  const commitDraftRow = async (/** @type {import('./field-input.js').FieldPatch} */ patch) => {
    setDraftRow(null);
    if (!patch || !labelField) return;
    const value = patch.apiPatch[labelField.name];
    const isEmpty = value === null || value === undefined || value === '' || (typeof value === 'object' && !Object.values(value).some(Boolean));
    if (isEmpty) return;
    try {
      const lastPosition = records.length ? Number(records[records.length - 1].position ?? records.length) : 0;
      const created = await createRecord(objectMetadata, { ...patch.apiPatch, position: lastPosition + 1 });
      insertRecordLocally(objectMetadata.nameSingular, created);
      setTotalCount((count) => (count ?? 0) + 1);
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  /** @param {TableColumn} column @param {PointerEvent} event */
  const startColumnResize = (column, event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = column.width;
    let latest = startWidth;
    /** @param {PointerEvent} moveEvent */
    const handleMove = (moveEvent) => {
      latest = isPartColumn(column) ? clampPartColumnWidth(startWidth + moveEvent.clientX - startX) : clampTableColumnWidth(startWidth + moveEvent.clientX - startX);
      setResizing({ fieldId: column.field.id, width: latest });
    };
    const handleUp = () => {
      isResizingColumn = false;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      setResizing(null);
      if (latest === startWidth) return;
      persistViewFields(view, isPartColumn(column)
        ? updateSubFieldColumn(view.viewFields, sourceFieldIdOf(column.field), column.subFieldName ?? null, { size: latest })
        : upsertViewField(view.viewFields, column.field.id, { size: latest }));
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  /** Soltar uma coluna antes de outra: a primeira coluna (nome) fica sempre no lugar. @param {string} targetFieldId */
  const dropColumn = (targetFieldId) => {
    const draggedFieldId = columnDrag.draggedFieldId;
    setColumnDrag({ draggedFieldId: null, overFieldId: null });
    if (!draggedFieldId || draggedFieldId === targetFieldId) return;
    const [labelColumn, ...rest] = baseColumns;
    const order = moveIdBefore(rest.map(tableColumnIdOf), draggedFieldId, targetFieldId);
    persistViewFields(view, applyColumnOrder(view.viewFields, [tableColumnIdOf(labelColumn), ...order]));
  };

  const draftForEmptyState = getViewDraft(view);
  // busca em qualquer campo também é filtro: a lista vazia diz "nenhum encontrado", não "adicione o primeiro"
  const hasFilters = draftForEmptyState.filters.value.length > 0 || draftForEmptyState.anyFieldSearch.value.trim() !== '';
  const isEmpty = !isLoading && records.length === 0 && !draftRow && (!isGrouped || displayedGroups.every((group) => (groupCounts.get(group.key) ?? 0) === 0));
  // a view só pede etapas que o papel não vê ("Leads ganhos" para a pré-venda): a lista vazia diz isso, não "nenhum
  // registro corresponde aos filtros"
  const stageField = objectMetadata.fields.find((field) => field.name === 'stage');
  const outOfScopeStages = isEmpty && !isTrashMode
    ? findStageFilterOutsideScope(objectMetadata.nameSingular, draftForEmptyState.filters.value, stageField?.id, currentUserPermissionsState.value)
    : null;
  const tableWidth = 32 + columns.reduce((sum, column) => sum + column.width, 0) + 32;

  /** @param {any} record */
  const renderRow = (record) => {
    const isSelected = selectedIds.has(record.id);
    return html`<tr key=${record.id} class="table-row" data-selected=${isSelected ? 'true' : undefined} data-record-id=${record.id}
      onContextMenu=${(/** @type {MouseEvent} */ event) => openContextMenu(event, record)}>
      <td class="table-cell table-checkbox-cell is-sticky-checkbox">
        <span class="table-checkbox-wrapper" data-visible=${isSelected ? 'true' : undefined}>
          <${Checkbox}
            checked=${isSelected}
            ariaLabel=${t('table.selectRow')}
            onChange=${(checked) => {
              const next = new Set(selectedIds);
              if (checked) next.add(record.id);
              else next.delete(record.id);
              setIsAllMatchingSelected(false);
              setSelectedIds(next);
              selectionAnchorRef.current = record.id;
            }}
          />
        </span>
      </td>
      ${columns.map((column, columnIndex) => {
        const isEditing = editingCell?.recordId === record.id && editingCell.fieldId === column.field.id;
        const isFocused = focusedCell?.recordId === record.id && focusedCell.columnIndex === columnIndex;
        const isReadOnly = isCellReadOnly(record, column);
        return html`<td
          key=${column.field.id}
          class=${`table-cell ${columnIndex === 0 ? 'is-sticky-label' : ''}`}
          data-editing=${isEditing ? 'true' : undefined}
          data-focused=${isFocused && !isEditing ? 'true' : undefined}
          data-readonly=${isReadOnly ? 'true' : undefined}
          data-field-name=${column.field.name}
          data-sub-field-name=${column.field.subFieldName ?? undefined}
          style=${{ width: `${column.width}px`, maxWidth: `${column.width}px` }}
          onClick=${(event) => {
            if (/** @type {HTMLElement} */ (event.target).closest('a, .rating-star')) return;
            setFocusedCell({ recordId: record.id, columnIndex });
            openCell(record, columnIndex);
          }}
        >
          <div class="table-cell-content">
            ${columnIndex === 0
              ? html`<${RecordChip} objectMetadata=${objectMetadata} record=${record} />`
              : column.field.type === FIELD_TYPE.RATING && !isFieldReadOnly(column.field) && !isTrashMode
                ? html`<${RatingStars} value=${ratingToNumber(record[column.field.name])} onSelect=${(rating) => commitPatch(record, getInputOnlyPatch(column.field, record, rating))} />`
                : html`<${FieldDisplay} field=${column.field} record=${record} />`}
          </div>
          ${isEditing
            ? html`<${FieldInput}
                field=${column.field}
                record=${record}
                initialCharacter=${editingCell?.initialCharacter}
                onCommit=${(patch, navigation) => {
                  setEditingCell(null);
                  commitPatch(record, patch);
                  moveFocus(record.id, columnIndex, navigation);
                }}
                onCancel=${() => {
                  setEditingCell(null);
                  moveFocus(record.id, columnIndex, null);
                }}
              />`
            : null}
        </td>`;
      })}
      <td class="table-cell"></td>
      <td class="table-cell table-filler-cell"></td>
    </tr>`;
  };

  /** Cabeçalho, linhas, "Carregar mais" e "+ Novo" de uma seção da tabela agrupada. @param {TableGroup} group */
  const renderGroupSection = (group) => {
    const isCollapsed = collapsedGroups.has(group.key);
    const groupRecords = recordsByGroup.get(group.key) ?? [];
    const count = groupCounts.get(group.key) ?? groupRecords.length;
    const page = groupPages.get(group.key);
    const toggle = () => setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(group.key)) next.delete(group.key);
      else next.add(group.key);
      return next;
    });
    return html`
      <tr key=${`group-${group.key}`} class="table-group-row" data-group-key=${group.key} data-collapsed=${isCollapsed ? 'true' : undefined}>
        <td class="table-cell is-sticky-checkbox"></td>
        <td class="table-cell is-sticky-label table-group-header-cell" style=${{ width: `${columns[0].width}px`, maxWidth: `${columns[0].width}px` }}>
          <div class="table-cell-content table-group-header">
            <button type="button" class="table-group-toggle" aria-expanded=${isCollapsed ? 'false' : 'true'}
              aria-label=${t('boardTable.table.toggleGroup', { group: group.label })} onClick=${toggle}>
              <${Icon} name="IconChevronDown" size=${14} />
            </button>
            <${Tag} color=${group.color} text=${group.label} variant=${group.value === null ? 'outline' : 'solid'} weight="medium" />
            <span class="table-group-count" data-testid="table-group-count">${formatNumber(count)}</span>
          </div>
        </td>
        <td class="table-cell table-group-filler" colspan=${columns.length + 1}></td>
      </tr>
      ${isCollapsed ? null : groupRecords.map(renderRow)}
      ${!isCollapsed && page?.hasNextPage
        ? html`<tr key=${`group-more-${group.key}`} class="table-row table-group-action-row">
            <td class="table-cell is-sticky-checkbox"></td>
            <td class="table-cell is-sticky-label" onClick=${() => loadMoreInGroup(group)}>
              <div class="table-cell-content table-add-new">
                <${Icon} name="IconChevronDown" size=${16} />
                <span>${page.isLoadingMore ? t('board.loadingMore') : t('board.loadMoreCount', { count: Math.max(0, count - groupRecords.length) })}</span>
              </div>
            </td>
            <td class="table-cell" colspan=${columns.length + 1}></td>
          </tr>`
        : null}
      ${!isCollapsed && canUpdate
        ? html`<tr key=${`group-new-${group.key}`} class="table-row table-add-row table-group-action-row">
            <td class="table-cell is-sticky-checkbox"></td>
            <td class="table-cell is-sticky-label" onClick=${() => onRequestCreate?.({ [/** @type {FieldMetadata} */ (groupField).name]: group.value })}>
              <div class="table-cell-content table-add-new">
                <${Icon} name="IconPlus" size=${16} />
                <span>${t('board.new')}</span>
              </div>
            </td>
            <td class="table-cell" colspan=${columns.length + 1}></td>
          </tr>`
        : null}
    `;
  };

  return html`<div class="record-table-container">
    <div
      class="record-table-scroll"
      ref=${scrollRef}
      tabindex="0"
      onKeyDown=${handleTableKeyDown}
      onScroll=${(event) => {
        const element = /** @type {HTMLDivElement} */ (event.currentTarget);
        const isScrolled = element.scrollLeft > 0 ? 'true' : 'false';
        if (element.dataset.scrolledX !== isScrolled) element.dataset.scrolledX = isScrolled;
      }}
    >
      <table class="record-table" data-grouped=${isGrouped ? 'true' : undefined} data-resizing=${resizing ? 'true' : undefined} style=${{ minWidth: `${tableWidth}px` }}>
        <thead>
          <tr>
            <th class="table-header-cell table-checkbox-cell is-sticky-checkbox">
              <span class="table-checkbox-wrapper">
                <${Checkbox}
                  checked=${allSelected}
                  indeterminate=${someSelected}
                  ariaLabel=${t('table.selectAll')}
                  onChange=${(checked) => {
                    if (checked) selectAllLoaded();
                    else clearSelection();
                  }}
                />
              </span>
            </th>
            ${columns.map(
              (column, columnIndex) => html`<${ColumnHeader}
                key=${column.field.id}
                objectMetadata=${objectMetadata}
                view=${view}
                column=${column}
                columnIndex=${column.isTrashColumn ? columnIndex : viewColumns.findIndex((candidate) => candidate.field.id === column.field.id)}
                columns=${viewColumns}
                onResizeStart=${startColumnResize}
                dragState=${columnDrag}
                onColumnDragStart=${(/** @type {string} */ fieldId) => setColumnDrag({ draggedFieldId: fieldId, overFieldId: null })}
                onColumnDragOver=${(/** @type {string} */ fieldId) => {
                  if (columnDrag.overFieldId !== fieldId) setColumnDrag({ ...columnDrag, overFieldId: fieldId });
                }}
                onColumnDrop=${dropColumn}
                onColumnDragEnd=${() => setColumnDrag({ draggedFieldId: null, overFieldId: null })}
              />`,
            )}
            <${AddColumnHeader} objectMetadata=${objectMetadata} view=${view} columns=${columns} />
            <th class="table-header-cell table-filler-cell"></th>
          </tr>
        </thead>
        <tbody>
          ${isLoading
            ? Array.from({ length: 8 }, (_, rowIndex) => html`<tr key=${`skeleton-${rowIndex}`} class="table-row">
                <td class="table-cell table-checkbox-cell is-sticky-checkbox"></td>
                ${columns.map((column, columnIndex) => html`<td class=${`table-cell ${columnIndex === 0 ? 'is-sticky-label' : ''}`}>
                  <div class="table-cell-content"><${Skeleton} width=${Math.max(40, column.width - 40 - ((rowIndex * 13 + columnIndex * 7) % 40))} height=${12} /></div>
                </td>`)}
                <td class="table-cell"></td><td class="table-cell table-filler-cell"></td>
              </tr>`)
            : isGrouped
              ? displayedGroups.map(renderGroupSection)
              : records.map(renderRow)}
          ${draftRow && labelField && !isGrouped
            ? html`<tr class="table-row">
                <td class="table-cell table-checkbox-cell is-sticky-checkbox"></td>
                <td class="table-cell is-sticky-label" data-editing="true" style=${{ width: `${columns[0].width}px` }}>
                  <div class="table-cell-content"></div>
                  <${FieldInput}
                    field=${labelField}
                    record=${draftRow}
                    onCommit=${(patch) => commitDraftRow(patch)}
                    onCancel=${() => setDraftRow(null)}
                  />
                </td>
                ${columns.slice(1).map((column) => html`<td key=${column.field.id} class="table-cell"></td>`)}
                <td class="table-cell"></td><td class="table-cell table-filler-cell"></td>
              </tr>`
            : null}
          ${!isLoading && pageInfo.hasNextPage && !isGrouped
            ? html`<tr ref=${sentinelRef} class="table-row table-loading-row">
                <td class="table-cell is-sticky-checkbox"></td>
                <td class="table-cell is-sticky-label" colspan="1"><div class="table-cell-content"><${Skeleton} width=${120} height=${12} /></div></td>
                <td class="table-cell" colspan=${columns.length + 1}></td>
              </tr>`
            : null}
          ${!isLoading && !isEmpty && !isGrouped && !isTrashMode && canUpdate && !isSnapshotScope
            ? html`<tr class="table-row table-add-row">
                <td class="table-cell is-sticky-checkbox"></td>
                <td class="table-cell is-sticky-label" colspan="1" onClick=${() => setDraftRow({ id: 'draft' })}>
                  <div class="table-cell-content table-add-new">
                    <${Icon} name="IconPlus" size=${16} />
                    <span>${t('table.addNew')}</span>
                  </div>
                </td>
                <td class="table-cell" colspan=${columns.length + 1}></td>
              </tr>`
            : null}
        </tbody>
        ${!isLoading && !isEmpty
          ? html`<tfoot>
              <tr class="table-footer-row">
                <td class="table-cell is-sticky-checkbox"></td>
                ${columns.map((column, columnIndex) => {
                  const operation = footerOperationOf(view, column);
                  return html`<${TableFooterCell}
                    key=${column.field.id}
                    view=${view}
                    column=${column}
                    columnIndex=${columnIndex}
                    operation=${operation}
                    value=${aggregateValues.get(column.field.id)}
                    totalCount=${totalCount ?? records.length}
                  />`;
                })}
                <td class="table-cell"></td>
                <td class="table-cell table-filler-cell"></td>
              </tr>
            </tfoot>`
          : null}
      </table>
      ${isEmpty
        ? html`<div class="record-table-empty">
            ${query.drilldown && !isTrashMode
              ? html`<${DrilldownEmptyState} parameter=${query.drilldown} error=${loadError} onClear=${() => clearViewDrilldown(view)} />`
              : isTrashMode
              ? html`<${EmptyState}
                  icon="IconTrash"
                  title=${t('trash.emptyTitle', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
                  subtitle=${hasFilters
                    ? t('trash.emptySubtitle')
                    : trashRetentionDaysState.value === null ? '' : t('trash.retentionNotice', { count: trashRetentionDaysState.value })}
                  action=${html`<${Button} variant="secondary" icon="IconFilterOff" title=${t('trash.removeChip')} onClick=${() => setDeletedFilterActive(view, false)} />`}
                />`
              : outOfScopeStages
              ? html`<${EmptyState}
                  icon="IconEyeOff"
                  title=${t('viewsFilters.stagesOutOfScope.title')}
                  subtitle=${t('viewsFilters.stagesOutOfScope.subtitle', {
                    count: outOfScopeStages.length,
                    stages: outOfScopeStages.map((value) => translateMetadataLabel(stageField?.options?.find((option) => option.value === value)?.label ?? value)).join(', '),
                  })}
                />`
              : hasFilters
              ? html`<${EmptyState}
                  icon="IconFilterOff"
                  title=${t('table.empty.noMatchTitle', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
                  subtitle=${t('table.empty.noMatchSubtitle')}
                  action=${html`<${Button} variant="secondary" icon="IconPlus" title=${t('table.empty.addRecord', { object: translateMetadataLabel(objectMetadata.labelSingular) })} onClick=${() => onRequestCreate?.()} />`}
                />`
              : html`<${EmptyState}
                  icon=${objectMetadata.icon ?? 'IconCube'}
                  title=${t('table.empty.firstRecordTitle', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
                  subtitle=${t('table.empty.firstRecordSubtitle', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
                  action=${html`<${Button} variant="secondary" icon="IconPlus" title=${t('table.empty.addRecord', { object: translateMetadataLabel(objectMetadata.labelSingular) })} onClick=${() => onRequestCreate?.()} />`}
                />`}
          </div>`
        : null}
    </div>
    ${contextMenu && selectedIds.size > 0
      ? html`<${RecordContextMenu}
          position=${contextMenu}
          onClose=${() => setContextMenu(null)}
          target=${{
            objectMetadata, view, count: selectedCount, isTrashMode, isAllMatchingSelected, resolveSelectedIds,
            onChanged: () => setReloadToken((token) => token + 1), onDeleted: removeLocally, onClear: clearSelection,
          }}
        />`
      : null}
    ${selectedIds.size > 0 && isTrashMode
      ? html`<${TrashSelectionActions}
          objectMetadata=${objectMetadata}
          view=${view}
          selectedCount=${selectedCount}
          resolveSelectedIds=${resolveSelectedIds}
          onClear=${clearSelection}
          onRemoved=${(/** @type {string[]} */ ids) => {
            const removed = new Set(ids);
            liveList.records.value = liveList.records.value.filter((record) => !removed.has(record.id));
            setTotalCount((count) => (count === null ? count : Math.max(0, count - ids.length)));
            clearSelection();
          }}
          selectAllPrompt=${allSelected && !isAllMatchingSelected && (totalCount ?? 0) > records.length
            ? html`<button type="button" class="light-button selection-select-all" onClick=${() => setIsAllMatchingSelected(true)}>
                ${t('parity.bulk.selectAllMatching', { count: totalCount ?? 0 })}
              </button>`
            : null}
        />`
      : selectedIds.size > 0
      ? html`<${RecordSelectionActions}
          objectMetadata=${objectMetadata}
          view=${view}
          selectedCount=${selectedCount}
          isAllMatchingSelected=${isAllMatchingSelected}
          resolveSelectedIds=${resolveSelectedIds}
          onClear=${clearSelection}
          onChanged=${() => setReloadToken((token) => token + 1)}
          onDeleted=${(/** @type {string[]} */ ids) => {
            const deleted = new Set(ids);
            liveList.records.value = liveList.records.value.filter((record) => !deleted.has(record.id));
            setTotalCount((count) => (count === null ? count : Math.max(0, count - ids.length)));
            clearSelection();
          }}
          selectAllPrompt=${!isGrouped && allSelected && !isAllMatchingSelected && (totalCount ?? 0) > records.length
            ? html`<button type="button" class="light-button selection-select-all" onClick=${() => setIsAllMatchingSelected(true)}>
                ${t('parity.bulk.selectAllMatching', { count: totalCount ?? 0 })}
              </button>`
            : null}
        />`
      : null}
  </div>`;
}
